import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiPlus, FiEdit2, FiTrash2, FiCheck, FiX, FiMaximize } from 'react-icons/fi';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';

const DealerSizeManager = () => {
  const { dealerId, userRole } = useAuth();
  const [sizes, setSizes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingSize, setEditingSize] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    width: '',
    depth: ''
  });

  useEffect(() => {
    if (dealerId) loadSizes();
  }, [dealerId]);

  const loadSizes = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('dealer_sizes')
        .select('*')
        .eq('dealer_id', dealerId)
        .order('width', { ascending: true });
      if (!error && data) setSizes(data);
    } catch (err) {
      console.error('Failed to load sizes:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const w = parseFloat(form.width);
    const d = parseFloat(form.depth);

    if (!w || w <= 0) {
      alert('Please enter a valid width.');
      return;
    }
    if (!d || d <= 0) {
      alert('Please enter a valid depth.');
      return;
    }

    const sizeLabel = `${w}x${d}`;

    // Check for duplicate
    const duplicate = sizes.find(s =>
      s.id !== editingSize?.id && s.size_label === sizeLabel
    );
    if (duplicate) {
      alert(`Size ${sizeLabel} already exists.`);
      return;
    }

    setSaving(true);
    try {
      if (editingSize) {
        const { error } = await supabase
          .from('dealer_sizes')
          .update({
            size_label: sizeLabel,
            width: w,
            depth: d
          })
          .eq('id', editingSize.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('dealer_sizes')
          .insert([{
            dealer_id: dealerId,
            size_label: sizeLabel,
            width: w,
            depth: d,
            is_active: true
          }]);
        if (error) throw error;
      }

      setShowForm(false);
      setEditingSize(null);
      setForm({ width: '', depth: '' });
      await loadSizes();
    } catch (err) {
      console.error('Failed to save size:', err);
      alert('Failed to save size: ' + (err.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (size) => {
    setEditingSize(size);
    setForm({
      width: size.width.toString(),
      depth: size.depth.toString()
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this size?')) return;
    try {
      const { error } = await supabase
        .from('dealer_sizes')
        .delete()
        .eq('id', id);
      if (!error) {
        setSizes(sizes.filter(s => s.id !== id));
      }
    } catch (err) {
      console.error('Failed to delete size:', err);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingSize(null);
    setForm({ width: '', depth: '' });
  };

  // Quick-add preset sizes
  const presetSizes = [
    [6,8],[8,8],[8,10],[8,12],[10,10],[10,12],[10,14],[10,16],
    [12,12],[12,16],[12,20],[12,24],[14,20],[14,28],[14,32],[14,36],
    [14,40],[16,24],[16,32],[16,36],[16,40]
  ];
  const existingLabels = new Set(sizes.map(s => s.size_label));
  const availablePresets = presetSizes.filter(([w,d]) => !existingLabels.has(`${w}x${d}`));

  const handleQuickAdd = async (w, d) => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('dealer_sizes')
        .insert([{
          dealer_id: dealerId,
          size_label: `${w}x${d}`,
          width: w,
          depth: d,
          is_active: true
        }]);
      if (error) throw error;
      await loadSizes();
    } catch (err) {
      console.error('Failed to quick-add size:', err);
    } finally {
      setSaving(false);
    }
  };

  if (!dealerId) {
    return (
      <div className="p-6 text-center text-gray-500">
        <FiMaximize className="w-12 h-12 mx-auto mb-4 text-gray-300" />
        <p>Size management is available for dealer accounts.</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Custom Sizes</h1>
          <p className="text-gray-600">Set up your shed sizes once. They appear in the size picker on every upload.</p>
        </div>
        <button
          onClick={() => { setEditingSize(null); setForm({ width: '', depth: '' }); setShowForm(true); }}
          className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg transition-colors"
        >
          <FiPlus className="w-5 h-5" />
          Add Size
        </button>
      </div>

      {/* Add/Edit Form */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden mb-6"
          >
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {editingSize ? 'Edit Size' : 'New Custom Size'}
              </h3>
              <div className="flex items-end gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Width (ft)</label>
                  <input
                    type="number"
                    step="1"
                    min="1"
                    value={form.width}
                    onChange={(e) => setForm({ ...form, width: e.target.value })}
                    placeholder="e.g., 12"
                    className="w-32 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 text-lg font-mono"
                  />
                </div>
                <span className="text-2xl font-bold text-gray-400 pb-2">×</span>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Depth (ft)</label>
                  <input
                    type="number"
                    step="1"
                    min="1"
                    value={form.depth}
                    onChange={(e) => setForm({ ...form, depth: e.target.value })}
                    placeholder="e.g., 24"
                    className="w-32 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 text-lg font-mono"
                  />
                </div>
                {form.width && form.depth && (
                  <div className="pb-2">
                    <span className="text-sm text-gray-500">
                      = {Math.round(parseFloat(form.width) * parseFloat(form.depth))} sq ft
                    </span>
                  </div>
                )}
                <div className="flex gap-2 ml-auto">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 text-white px-5 py-2 rounded-lg transition-colors"
                  >
                    {saving ? (
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <FiCheck className="w-4 h-4" />
                    )}
                    {editingSize ? 'Update' : 'Save'}
                  </button>
                  <button
                    onClick={handleCancel}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
                  >
                    <FiX className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Quick-Add Presets */}
      {availablePresets.length > 0 && sizes.length < 5 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
          <h4 className="text-sm font-medium text-blue-800 mb-2">Quick Add Popular Sizes</h4>
          <div className="flex flex-wrap gap-2">
            {availablePresets.slice(0, 12).map(([w, d]) => (
              <button
                key={`${w}x${d}`}
                onClick={() => handleQuickAdd(w, d)}
                disabled={saving}
                className="px-3 py-1.5 bg-white border border-blue-200 text-blue-700 text-sm font-mono rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-50"
              >
                {w}x{d}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Sizes Grid */}
      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl h-24 animate-pulse" />
          ))}
        </div>
      ) : sizes.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl shadow-sm">
          <FiMaximize className="w-16 h-16 text-gray-200 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-600 mb-2">No custom sizes yet</h3>
          <p className="text-gray-400 mb-6 max-w-md mx-auto">
            Add the shed sizes you carry. They'll show up in the size picker when you upload sheds.
          </p>
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2.5 rounded-lg transition-colors"
          >
            <FiPlus className="w-5 h-5" />
            Add Your First Size
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {sizes.map((size) => (
            <motion.div
              key={size.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 text-center hover:shadow-md transition-shadow group relative"
            >
              <div className="text-2xl font-bold text-gray-900 font-mono">{size.size_label}</div>
              <div className="text-xs text-gray-400 mt-1">{Math.round(size.width * size.depth)} sq ft</div>

              {/* Hover actions */}
              <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity flex gap-0.5">
                <button
                  onClick={() => handleEdit(size)}
                  className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors text-gray-400 hover:text-gray-700"
                >
                  <FiEdit2 className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => handleDelete(size.id)}
                  className="p-1.5 hover:bg-red-50 rounded-lg transition-colors text-gray-400 hover:text-red-600"
                >
                  <FiTrash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DealerSizeManager;
